import { getCharacterForPhase } from '../content/characters';
import { FIELD_WIDTH } from '../../config/gameplay';
import type { CardUseRequest, SessionState } from '../simulation/types';
import { planAutoDrop, type AutoBody } from './AutoPlayPlanner';

type MovingBody = AutoBody & { speed: number };
export type AutoPlayOptions = {
  getState: () => SessionState;
  getBodies: () => readonly MovingBody[];
  canControl: () => boolean;
  dropAt: (x: number) => void;
  useCard: (request: CardUseRequest) => void;
  onEnabled: (enabled: boolean) => void;
};

export class AutoPlayController {
  public enabled = false;
  private turn = '';
  private readyAt = 0;
  private cardUsed = false;

  public constructor(private readonly options: AutoPlayOptions) {}

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.reset();
    this.options.onEnabled(enabled);
  }

  public reset(): void {
    this.turn = '';
    this.cardUsed = false;
  }

  public update(nowMs: number): void {
    if (!this.enabled) return;
    const state = this.options.getState();
    if (state.status !== 'playing' || !this.options.canControl()) return;
    const turn = `${state.generation}:${state.dropSequence}:${state.activeDropEntityId}`;
    if (turn !== this.turn) {
      this.turn = turn;
      this.readyAt = nowMs;
      this.cardUsed = false;
      return;
    }
    const elapsed = nowMs - this.readyAt;
    if (elapsed < 90) return;
    const bodies = this.options.getBodies();
    // Give ongoing collisions a short chance to finish, but never wait indefinitely.
    if (elapsed < 650 && bodies.some(body => body.speed > 1.6)) return;
    const active = state.entities.find(entity => entity.entityId === state.activeDropEntityId);
    const id = active?.characterId ?? state.currentCharacterId;
    const nextId = state.dropView.slotActive ? state.dropView.currentCharacterId : state.dropView.nextCharacterId;
    const plan = planAutoDrop(id, nextId, bodies, state.phase, state.mode);
    if (!this.cardUsed) {
      for (const held of state.card.hand) {
        if (!state.cardAvailability.some(card => card.instanceId === held.instanceId && card.enabled)) continue;
        let targetEntityId: string | null = active?.entityId ?? null;
        let use = false;
        if (held.cardId === 'swap' && active) {
          const swapped = planAutoDrop(nextId, id, bodies, state.phase, state.mode);
          use = swapped.merges > 0 && swapped.score > plan.score + 80;
        } else if (held.cardId === 'destroy') {
          const last = bodies.find(body => body.entityId === state.dropView.lastPlacedEntityId);
          if (last) {
            const size = getCharacterForPhase(last.characterId, state.phase, state.mode).baseSize;
            const unsafe = last.y < size && (last.x < size || last.x > FIELD_WIDTH - size);
            const cleared = planAutoDrop(id, nextId, bodies.filter(body => body !== last), state.phase, state.mode);
            use = unsafe || (cleared.merges > plan.merges && cleared.score > plan.score + 160);
            targetEntityId = last.entityId;
          }
        } else if (held.cardId === 'reincarnate' && active) {
          use = plan.merges === 0 && plan.y < 120 && state.card.stones >= 4;
        }
        if (use) {
          this.cardUsed = true;
          this.readyAt = nowMs;
          this.options.useCard({ generation: state.generation, instanceId: held.instanceId, targetEntityId });
          return;
        }
      }
    }
    this.options.dropAt(plan.x);
  }
}
